import { computed, inject, toValue } from 'vue'
import { useClosetStore } from '@shared/stores/closet.js'
import { useCollectionsStore } from '@shared/stores/collections.js'

export default function useStyleColor (styleColor) {
  const closetStore = useClosetStore()
  const collectionsStore = useCollectionsStore()

  const useRealLooks = inject('useRealLooks', false)

  const id = computed(() => toValue(styleColor).id)
  const style = computed(() => toValue(styleColor).style)
  const brand = computed(() => style.value.brand)
  const name = computed(() => style.value.name)
  const frontImage = computed(() => toValue(styleColor).images.front)
  const realLookImages = computed(() => toValue(styleColor).images.realLook)
  const useRealLook = computed(() => toValue(useRealLooks) === true)
  const realLookImage = computed(() => useRealLook.value ? realLookImages.value?.[0] : null)
  const previewImage = computed(() => {
    const image = realLookImage.value ?? frontImage.value
    return image
      ? {
          ...image,
          url: image.url.replace('_d', '_m')
        }
      : null
  })
  const backImage = computed(() => toValue(styleColor).images.back)
  const discontinued = computed(() => toValue(styleColor).discontinued ?? false)
  const itemTypes = computed(() => toValue(styleColor).itemTypes)
  const unavailable = computed(() => !itemTypes.value.some(itemType => [true, undefined].includes(itemType.isMemberSize) && itemType.numAvailable > 0))
  const stocking = computed(() => itemTypes.value.some(itemType => itemType.stocking))
  const retailPrice = computed(() => Math.round(style.value.retailPrice))
  const minPrice = computed(() => toValue(styleColor).priceRange?.minPrice)
  const maxPrice = computed(() => toValue(styleColor).priceRange?.maxPrice)
  const hasPriceRange = computed(() => !!minPrice.value && !!maxPrice.value && minPrice.value !== maxPrice.value)
  const latestReturnDate = computed(() => toValue(styleColor).latestReturnDate ? new Date(toValue(styleColor).latestReturnDate) : null)
  const firstAvailableDate = computed(() => toValue(styleColor).firstAvailable ? new Date(toValue(styleColor).firstAvailable) : null)
  const pairsWith = computed(() => toValue(styleColor)?.pairsWith || [])

  const rating = computed(() => closetStore.ratingsMap[toValue(styleColor).id])
  const isFavorited = computed(() => rating.value === 1)
  const isCollected = computed(() => isFavorited.value || collectionsStore.collectedStyleColors.includes(toValue(styleColor).id))
  const isDisliked = computed(() => rating.value === -1)

  async function recordClick (source, sourceIndex) {
    await closetStore.recordClick({ styleColorId: toValue(styleColor).id, source, sourceIndex })
  }

  return {
    id,
    style,
    brand,
    name,
    frontImage,
    realLookImages,
    realLookImage,
    useRealLook,
    previewImage,
    backImage,
    discontinued,
    itemTypes,
    unavailable,
    stocking,
    retailPrice,
    minPrice,
    maxPrice,
    hasPriceRange,
    latestReturnDate,
    firstAvailableDate,
    pairsWith,
    rating,
    isFavorited,
    isCollected,
    isDisliked,
    recordClick
  }
}
